import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DiaryEntryList from '../components/diary/DiaryEntryList';
import DiaryEntryForm from '../components/diary/DiaryEntryForm';
import DiarySummary from '../components/diary/DiarySummary';
import DiaryGoals from '../components/diary/DiaryGoals';
import { discardWaiting, onRecordsSent, overlay } from '../utils/offline';
import '../components/diary/Diary.css';

// The gymnast's own training diary (or a guardian reading their child's).
export default function MyDiary() {
  const [gymnasts, setGymnasts] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [diary, setDiary] = useState(null);
  const [editing, setEditing] = useState(null); // null, 'new' or an entry
  const [summaryKey, setSummaryKey] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/training-diary/mine')
      .then(res => {
        setGymnasts(res.data);
        if (res.data.length > 0) setSelectedId(res.data[0].id);
      })
      .catch(() => setError('Could not load your diary.'));
  }, []);

  // With no signal and nothing saved on this phone yet, past sessions can't be
  // shown, but new ones can still be logged and are sent later
  const loadEntries = useCallback(() => {
    if (!selectedId) return;
    const path = `/api/training-diary/gymnasts/${selectedId}/entries`;
    axios.get(path)
      .then(res => setDiary(res.data))
      .catch(async () => {
        const gymnast = gymnasts?.find(g => g.id === selectedId);
        const waiting = await overlay(path, { entries: [] });
        setDiary({ entries: waiting.entries, canWrite: Boolean(gymnast?.canWrite), notLoaded: true });
      });
  }, [selectedId, gymnasts]);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => onRecordsSent(kind => {
    if (kind !== 'diary') return;
    loadEntries();
    setSummaryKey(k => k + 1);
  }), [loadEntries]);

  const saved = () => {
    setEditing(null);
    loadEntries();
    setSummaryKey(k => k + 1);
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this session from the diary?')) return;
    try {
      await axios.delete(`/api/training-diary/entries/${entry.id}`);
      saved();
    } catch {
      setError('Could not delete the entry.');
    }
  };

  const discard = async (entry) => {
    if (!window.confirm("Discard this session? It hasn't been saved.")) return;
    await discardWaiting('diary', entry.id);
    loadEntries();
  };

  if (error) return <div className="container"><p className="bk-error">{error}</p></div>;
  if (!gymnasts) return <div className="loading"><div className="spinner" /></div>;
  if (gymnasts.length === 0) {
    return <div className="container"><h2>Training diary</h2><p>Your coach hasn't turned on a training diary for you yet.</p></div>;
  }

  const selected = gymnasts.find(g => g.id === selectedId);
  return (
    <div className="container diary-page">
      <div className="diary-page__head">
        <h2>Training diary{gymnasts.length > 1 ? '' : `: ${selected.firstName}`}</h2>
        {gymnasts.length > 1 && (
          <label>Whose diary?{' '}
            <select className="form-control" value={selectedId} onChange={e => { setDiary(null); setSelectedId(e.target.value); }}>
              {gymnasts.map(g => <option key={g.id} value={g.id}>{g.firstName} {g.lastName}</option>)}
            </select>
          </label>
        )}
        {diary?.canWrite && (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>Log a session</button>
        )}
      </div>

      <DiaryGoals gymnastId={selectedId} />

      {diary?.notLoaded && (
        <p className="text-muted">Past sessions can't be shown without signal.{diary.canWrite && ' You can still log a session.'}</p>
      )}
      {diary && !(diary.notLoaded && diary.entries.length === 0) && (
        <DiaryEntryList entries={diary.entries} canWrite={diary.canWrite} onEdit={setEditing} onDelete={remove} onDiscard={discard} />
      )}
      <div style={{ marginTop: '1.5rem' }}>
        <DiarySummary key={`${selectedId}-${summaryKey}`} gymnastId={selectedId} />
      </div>

      {editing && (
        <DiaryEntryForm
          gymnastId={selectedId}
          entry={editing === 'new' ? undefined : editing}
          onSaved={saved}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}
